import * as React from "react";
import { cn } from "../../lib/utils";
import { HelpCircle } from "lucide-react";

export interface ModuleScoreProps {
  /** Score value (0-100) */
  score?: number;
  /** Grade letter (A-F) */
  grade?: string;
  /** Size variant */
  size?: "sm" | "md" | "lg";
  /** Show as circular gauge */
  variant?: "badge" | "gauge";
  /** Custom class name */
  className?: string;
  /** Show tooltip with explanation */
  showTooltip?: boolean;
  /** Custom tooltip text */
  tooltipText?: string;
}

// Get color based on score
function getScoreColor(score: number): {
  text: string;
  bg: string;
  border: string;
  gradient: string;
} {
  if (score >= 80) {
    return {
      text: "text-emerald-400",
      bg: "bg-emerald-500/15",
      border: "border-emerald-500/40",
      gradient: "from-emerald-500 to-emerald-400",
    };
  }
  if (score >= 60) {
    return {
      text: "text-cyan-400",
      bg: "bg-cyan-500/15",
      border: "border-cyan-500/40",
      gradient: "from-cyan-500 to-cyan-400",
    };
  }
  if (score >= 40) {
    return {
      text: "text-amber-400",
      bg: "bg-amber-500/15",
      border: "border-amber-500/40",
      gradient: "from-amber-500 to-amber-400",
    };
  }
  if (score >= 20) {
    return {
      text: "text-orange-400",
      bg: "bg-orange-500/15",
      border: "border-orange-500/40",
      gradient: "from-orange-500 to-orange-400",
    };
  }
  return {
    text: "text-red-400",
    bg: "bg-red-500/15",
    border: "border-red-500/40",
    gradient: "from-red-500 to-red-400",
  };
}

// Get color based on grade
function getGradeColor(grade: string): {
  text: string;
  bg: string;
  border: string;
} {
  const gradeUpper = grade.toUpperCase();
  if (gradeUpper === "A" || gradeUpper === "A+") {
    return {
      text: "text-emerald-400",
      bg: "bg-emerald-500/15",
      border: "border-emerald-500/40",
    };
  }
  if (gradeUpper === "B" || gradeUpper === "B+") {
    return {
      text: "text-cyan-400",
      bg: "bg-cyan-500/15",
      border: "border-cyan-500/40",
    };
  }
  if (gradeUpper === "C" || gradeUpper === "C+") {
    return {
      text: "text-amber-400",
      bg: "bg-amber-500/15",
      border: "border-amber-500/40",
    };
  }
  if (gradeUpper === "D" || gradeUpper === "D+") {
    return {
      text: "text-orange-400",
      bg: "bg-orange-500/15",
      border: "border-orange-500/40",
    };
  }
  return {
    text: "text-red-400",
    bg: "bg-red-500/15",
    border: "border-red-500/40",
  };
}

// Badge variant
function ScoreBadge({
  score,
  grade,
  size,
  className,
}: Pick<ModuleScoreProps, "score" | "grade" | "size" | "className">) {
  const sizeClasses = {
    sm: "px-2 py-0.5 text-xs",
    md: "px-2.5 py-1 text-sm",
    lg: "px-3 py-1.5 text-base",
  };

  if (grade) {
    const colors = getGradeColor(grade);
    return (
      <span
        className={cn(
          "inline-flex items-center font-bold rounded-md border",
          colors.text,
          colors.bg,
          colors.border,
          sizeClasses[size || "md"],
          className
        )}
      >
        {grade.toUpperCase()}
      </span>
    );
  }

  if (score !== undefined) {
    const colors = getScoreColor(score);
    return (
      <span
        className={cn(
          "inline-flex items-center font-mono font-bold rounded-md border",
          colors.text,
          colors.bg,
          colors.border,
          sizeClasses[size || "md"],
          className
        )}
      >
        {score}
      </span>
    );
  }

  return null;
}

// Circular gauge variant
function ScoreGauge({
  score,
  size,
  className,
}: Pick<ModuleScoreProps, "score" | "size" | "className">) {
  const [animatedScore, setAnimatedScore] = React.useState(0);
  const gradientId = React.useId();

  // Animate score on mount
  React.useEffect(() => {
    if (score === undefined) return;

    const duration = 1000;
    const startTime = performance.now();
    const startScore = 0;

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setAnimatedScore(Math.round(startScore + (score - startScore) * easeOut));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [score]);

  if (score === undefined) return null;

  const colors = getScoreColor(score);
  const sizeConfig = {
    sm: { size: 48, stroke: 4, fontSize: "text-sm" },
    md: { size: 80, stroke: 6, fontSize: "text-xl" },
    lg: { size: 120, stroke: 8, fontSize: "text-3xl" },
  };

  const config = sizeConfig[size || "md"];
  const radius = (config.size - config.stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (animatedScore / 100) * circumference;

  return (
    <div className={cn("relative inline-flex items-center justify-center", className)}>
      <svg
        width={config.size}
        height={config.size}
        className="transform -rotate-90"
      >
        {/* Background circle */}
        <circle
          cx={config.size / 2}
          cy={config.size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={config.stroke}
          className="text-muted/30"
        />
        {/* Progress circle */}
        <circle
          cx={config.size / 2}
          cy={config.size / 2}
          r={radius}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth={config.stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="transition-all duration-1000 ease-out"
        />
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" className={cn("stop-current", colors.text)} />
            <stop offset="100%" className={cn("stop-current", colors.text)} style={{ opacity: 0.6 }} />
          </linearGradient>
        </defs>
      </svg>
      {/* Center text */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={cn("font-mono font-bold", colors.text, config.fontSize)}>
          {animatedScore}
        </span>
        {size !== "sm" && (
          <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
            Score
          </span>
        )}
      </div>
    </div>
  );
}

// Default tooltip text explaining score methodology
const DEFAULT_TOOLTIP_TEXT =
  "Security score (0-100) calculated from configuration analysis, " +
  "vulnerability checks, and best practice compliance. " +
  "80+ = Good, 60-79 = Fair, 40-59 = Needs Improvement, <40 = Critical.";

// Score tooltip component
function ScoreTooltip({
  children,
  text = DEFAULT_TOOLTIP_TEXT
}: {
  children: React.ReactNode;
  text?: string;
}) {
  const [isVisible, setIsVisible] = React.useState(false);

  return (
    <div className="relative inline-flex items-center gap-1">
      {children}
      <button
        type="button"
        className="text-muted-foreground hover:text-foreground transition-colors"
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onFocus={() => setIsVisible(true)}
        onBlur={() => setIsVisible(false)}
        aria-label="Score calculation explanation"
      >
        <HelpCircle className="h-3.5 w-3.5" />
      </button>
      {isVisible && (
        <div
          className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2
                     text-xs text-foreground bg-popover border border-border rounded-md
                     shadow-lg max-w-[250px] whitespace-normal"
          role="tooltip"
        >
          {text}
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-border" />
        </div>
      )}
    </div>
  );
}

export function ModuleScore({
  score,
  grade,
  size = "md",
  variant = "badge",
  className,
  showTooltip = false,
  tooltipText,
}: ModuleScoreProps) {
  const scoreElement = variant === "gauge"
    ? <ScoreGauge score={score} size={size} className={className} />
    : <ScoreBadge score={score} grade={grade} size={size} className={className} />;

  if (showTooltip) {
    return <ScoreTooltip text={tooltipText}>{scoreElement}</ScoreTooltip>;
  }

  return scoreElement;
}

// Score bar component for inline display
export interface ScoreBarProps {
  score: number;
  max?: number;
  label?: string;
  showValue?: boolean;
  size?: "sm" | "md";
  className?: string;
}

export function ScoreBar({
  score,
  max = 100,
  label,
  showValue = true,
  size = "md",
  className,
}: ScoreBarProps) {
  const percentage = Math.min(100, (score / max) * 100);
  const colors = getScoreColor(percentage);

  const heightClass = size === "sm" ? "h-1.5" : "h-2";

  return (
    <div className={cn("space-y-1", className)}>
      {(label || showValue) && (
        <div className="flex items-center justify-between text-xs">
          {label && <span className="text-muted-foreground">{label}</span>}
          {showValue && (
            <span className={cn("font-mono", colors.text)}>
              {score}
              {max !== 100 && `/${max}`}
            </span>
          )}
        </div>
      )}
      <div className={cn("w-full bg-muted/50 rounded-full overflow-hidden", heightClass)}>
        <div
          className={cn(
            "h-full rounded-full bg-gradient-to-r transition-all duration-500",
            colors.gradient
          )}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

ModuleScore.displayName = "ModuleScore";
ScoreBar.displayName = "ScoreBar";

// =============================================================================
// GRADE CARD - Security rating display with descriptive label (no letter grades)
// =============================================================================

export interface GradeCardProps {
  /** Grade letter (A-F, A+, etc.) - used internally to determine colors */
  grade: string;
  /** Optional numeric score (0-100) */
  score?: number;
  /** Size variant */
  size?: "sm" | "md" | "lg";
  /** Custom class name */
  className?: string;
}

/** Get descriptive label for a grade - user-friendly, no letter grades */
function getGradeLabel(grade: string): string {
  const labels: Record<string, string> = {
    "A+": "Excellent",
    A: "Very Good",
    B: "Good",
    C: "Fair",
    D: "Poor",
    F: "Needs Work",
  };
  return labels[grade.toUpperCase()] || labels.F;
}

/**
 * Security rating card with descriptive label and optional numeric score.
 * Shows user-friendly labels like "Excellent", "Good", "Needs Work" instead of letter grades.
 */
export function GradeCard({ grade, score, size = "md", className }: GradeCardProps) {
  const colors = getGradeColor(grade);
  const label = getGradeLabel(grade);

  const sizeConfig = {
    sm: { label: "text-sm font-semibold", score: "text-lg font-bold", container: "p-2" },
    md: { label: "text-sm font-semibold", score: "text-2xl font-bold", container: "p-3" },
    lg: { label: "text-base font-semibold", score: "text-3xl font-bold", container: "p-4" },
  };

  const config = sizeConfig[size];

  return (
    <div
      className={cn(
        "flex flex-col items-center gap-1 rounded-xl border-2",
        colors.bg,
        colors.border,
        config.container,
        className
      )}
    >
      {/* Numeric score as primary display */}
      {score !== undefined && (
        <span className={cn("font-mono", colors.text, config.score)}>
          {score}
        </span>
      )}
      {/* Descriptive label */}
      <span className={cn(colors.text, config.label)}>
        {label}
      </span>
      {/* Score context */}
      {score !== undefined && (
        <span className="text-[10px] text-muted-foreground">
          out of 100
        </span>
      )}
    </div>
  );
}

GradeCard.displayName = "GradeCard";

// =============================================================================
// RECOMMENDATION PRIORITY - Unified priority labels and colors
// =============================================================================

export type RecommendationPriority = "high" | "medium" | "low";

export interface RecommendationPriorityConfig {
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  iconColor: string;
}

/**
 * Get unified recommendation priority styling.
 * Uses "Important/Suggested/Optional" labels instead of alarming "High/Medium/Low".
 */
export function getRecommendationPriorityConfig(
  priority: RecommendationPriority
): RecommendationPriorityConfig {
  const configs: Record<RecommendationPriority, RecommendationPriorityConfig> = {
    high: {
      label: "Important",
      color: "text-orange-400",
      bgColor: "bg-orange-500/15",
      borderColor: "border-orange-500/40",
      iconColor: "text-orange-400",
    },
    medium: {
      label: "Suggested",
      color: "text-amber-400",
      bgColor: "bg-amber-500/15",
      borderColor: "border-amber-500/40",
      iconColor: "text-amber-400",
    },
    low: {
      label: "Optional",
      color: "text-slate-400",
      bgColor: "bg-slate-500/15",
      borderColor: "border-slate-500/40",
      iconColor: "text-slate-400",
    },
  };
  return configs[priority];
}
